import React, { useState } from 'react';
import { addMonths, parseISO } from 'date-fns';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  UseOfForceAuthorizationType,
  UseOfForceServiceProviderUnitModel,
} from '../../../../api-service/clients';
import {
  DatePicker,
  DatePickerValue,
} from '../../../../components/date-picker';
import { formatDate } from '../../../../utilities';
import {
  decisionMakerFor,
  maxPeriodMonths,
} from './use-of-force-authorization-rules';
import {
  authorizationDecisionMakerLabels,
  authorizationTypeLabels,
} from './use-of-force-labels';

export interface UseOfForceAuthorizationRegistration {
  serviceProviderUnitId?: string;
  authorizationType: UseOfForceAuthorizationType;
  periodFrom: string;
  periodTo: string;
}

interface UseOfForceAuthorizationRegistrationDialogProps {
  // Only the myndighed chooses the unit; the leader registers for their own.
  serviceProviderUnits?: UseOfForceServiceProviderUnitModel[];
  onSave: (registration: UseOfForceAuthorizationRegistration) => Promise<void>;
  onClose: () => void;
}

export function UseOfForceAuthorizationRegistrationDialog(
  props: UseOfForceAuthorizationRegistrationDialogProps
) {
  const { serviceProviderUnits, onSave, onClose } = props;
  const [serviceProviderUnitId, setServiceProviderUnitId] = useState(
    serviceProviderUnits?.length === 1 ? serviceProviderUnits[0].id : ''
  );
  const [authorizationType, setAuthorizationType] =
    useState<UseOfForceAuthorizationType>();
  const [periodFrom, setPeriodFrom] = useState<string>();
  const [periodTo, setPeriodTo] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);

  const latestPeriodTo =
    periodFrom && authorizationType
      ? addMonths(parseISO(periodFrom), maxPeriodMonths(authorizationType))
      : undefined;
  const isPeriodValid =
    !!periodFrom &&
    !!periodTo &&
    periodTo >= periodFrom &&
    (!latestPeriodTo || parseISO(periodTo) <= latestPeriodTo);
  const canSave =
    !isSaving &&
    !!authorizationType &&
    isPeriodValid &&
    (!serviceProviderUnits || !!serviceProviderUnitId);

  async function save() {
    if (!authorizationType || !periodFrom || !periodTo) return;
    setIsSaving(true);
    try {
      await onSave({
        serviceProviderUnitId: serviceProviderUnitId || undefined,
        authorizationType,
        periodFrom,
        periodTo,
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Registrér afgørelse</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Registrér en afgørelse om forhåndsgodkendelse ud fra afgørelsesbrevet.
        </Typography>
        {serviceProviderUnits && (
          <TextField
            select
            label="Udførerenhed"
            value={serviceProviderUnitId}
            onChange={event => setServiceProviderUnitId(event.target.value)}
            fullWidth
            required
            sx={{ mb: 2 }}
            helperText={
              serviceProviderUnits.length === 0
                ? 'Borgeren har ingen nuværende indsatser hos en udførerenhed.'
                : undefined
            }
          >
            {serviceProviderUnits.map(unit => (
              <MenuItem key={unit.id} value={unit.id}>
                {unit.name ?? unit.id}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          label="Type af indgreb"
          value={authorizationType ?? ''}
          onChange={event =>
            setAuthorizationType(
              event.target.value as UseOfForceAuthorizationType
            )
          }
          fullWidth
          required
        >
          {Object.values(UseOfForceAuthorizationType).map(type => (
            <MenuItem key={type} value={type}>
              {authorizationTypeLabels[type]}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <DatePicker
            label="Fra dato"
            value={periodFrom}
            onChange={value => setPeriodFrom(asPlainDate(value))}
            variant="outlined"
          />
          <DatePicker
            label="Til dato"
            value={periodTo}
            maxDate={latestPeriodTo}
            onChange={value => setPeriodTo(asPlainDate(value))}
            variant="outlined"
          />
        </Box>
        {authorizationType && (
          <Typography variant="body2" sx={{ mt: 2 }}>
            Truffet af:{' '}
            {
              authorizationDecisionMakerLabels[
                decisionMakerFor(authorizationType)
              ]
            }
            . Afgørelsen kan højst gælde i {maxPeriodMonths(authorizationType)}{' '}
            måneder.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annullér</Button>
        <Button variant="contained" disabled={!canSave} onClick={save}>
          Gem afgørelse
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Sent as a plain date so the day does not shift with the time zone.
function asPlainDate(value: DatePickerValue): string | undefined {
  return value ? formatDate(value, 'yyyy-MM-dd') : undefined;
}
